import type { DetailedSurvey } from "@survey-types/survey.core.types";
import type { LeaseComp } from "@survey-types/survey.leaseComp.types";
import type { Property } from "@survey-types/survey.property.types";
import { hasAddressLine2AsCustom } from "@utils/index";

type CombinedLegendItem =
  | { kind: "property"; item: Property }
  | { kind: "lease"; item: LeaseComp };

export interface LegendPageData {
  pageProperties: Property[];
  pageLeaseComps: LeaseComp[];
  numberedPropertiesBeforePage: number;
  startLeaseIndexFrom: number;
  hasPageContent: boolean;
}

interface UseMarketMapLegendPagesReturn {
  totalItemsCount: number;
  totalPages: number;
  estimatedLegendHeight: number;
  getPageData: (pageIndex: number) => LegendPageData;
}

export const useMarketMapLegendPages = (
  survey: DetailedSurvey | null,
  visibleProperties: Property[],
  visibleLeaseComps: LeaseComp[],
  mode?: string,
): UseMarketMapLegendPagesReturn => {
  const isPortrait =
    mode === "FlyerPreview" || survey?.pdfPaperSize === "Portrait";
  const hasHeader = !!survey?.isIncludeHeader;
  const displayFields = survey?.customLegends?.displayFields;
  const isAddressLine2Custom = survey ? hasAddressLine2AsCustom(survey) : true;
  const hasAddressLine2 = !!displayFields?.hasAddressLine2;
  const hasAddressLine2InLegend = hasAddressLine2 && !isAddressLine2Custom;
  const hasPropertyName = !!displayFields?.hasPropertyName;
  const hasAddressLine1 = !!displayFields?.hasAddressLine1;
  const hasCity = !!displayFields?.hasCity;
  const hasState = !!displayFields?.hasState;
  const hasZipCode = !!displayFields?.hasZipCode;

  const hasCityStateZip = hasCity && hasState && hasZipCode;
  const hasAddressLine1AsSingleLine =
    hasAddressLine1 && hasAddressLine2InLegend;
  const hasAddressLine1WithCityStateZip =
    hasAddressLine1 && !hasAddressLine2InLegend;
  const hasBottomLineField =
    hasCity || hasState || hasZipCode || hasAddressLine1WithCityStateZip;
  const hasTopLineField = hasPropertyName || hasAddressLine1AsSingleLine;
  const topLineFieldCount = [
    hasPropertyName,
    hasAddressLine1AsSingleLine,
    hasAddressLine2InLegend,
  ].filter(Boolean).length;
  const hasTwoTopLineFields = topLineFieldCount >= 2;
  const hasAnyLegendTextField = hasTopLineField || hasBottomLineField;

  let linesPerItem = 0;
  if (
    hasPropertyName &&
    hasAddressLine1AsSingleLine &&
    hasAddressLine2InLegend &&
    hasCityStateZip
  ) {
    linesPerItem = 3;
  } else if ((hasBottomLineField && hasTopLineField) || hasTwoTopLineFields) {
    linesPerItem = 2;
  } else if (hasAnyLegendTextField) {
    linesPerItem = 1;
  }

  let ITEMS_PER_PAGE = 0;
  if (hasHeader) {
    if (linesPerItem === 3) {
      ITEMS_PER_PAGE = isPortrait ? 8 : 10;
    } else if (linesPerItem === 2) {
      ITEMS_PER_PAGE = isPortrait ? 12 : 14;
    } else {
      ITEMS_PER_PAGE = isPortrait ? 14 : 24;
    }
  } else if (linesPerItem === 3) {
    ITEMS_PER_PAGE = isPortrait ? 14 : 11;
  } else if (linesPerItem === 2) {
    ITEMS_PER_PAGE = isPortrait ? 18 : 16;
  } else {
    ITEMS_PER_PAGE = isPortrait ? 18 : 27;
  }

  const combinedLegendItems: CombinedLegendItem[] = [
    ...visibleProperties.map((p) => ({ kind: "property" as const, item: p })),
    ...visibleLeaseComps.map((lc) => ({ kind: "lease" as const, item: lc })),
  ];

  const totalItemsCount = combinedLegendItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItemsCount / ITEMS_PER_PAGE));

  const firstPageItemsCount = Math.min(ITEMS_PER_PAGE, totalItemsCount);

  const legendRows = Math.ceil(firstPageItemsCount / 2);

  const rowHeight = linesPerItem <= 1 ? 33.25 : linesPerItem === 2 ? 52.75 : 70;

  const estimatedLegendHeight = survey?.isIncludeLegend
    ? legendRows * rowHeight + 32
    : 0;

  const getPageData = (pageIndex: number): LegendPageData => {
    const startIndex = pageIndex * ITEMS_PER_PAGE;
    const endIndex = Math.min(
      startIndex + ITEMS_PER_PAGE,
      combinedLegendItems.length,
    );
    const pageItems = combinedLegendItems.slice(startIndex, endIndex);

    const pageProperties: Property[] = [];
    const pageLeaseComps: LeaseComp[] = [];

    for (const entry of pageItems) {
      if (entry.kind === "property") {
        pageProperties.push(entry.item);
      } else {
        pageLeaseComps.push(entry.item);
      }
    }

    const itemsBeforePage = combinedLegendItems.slice(0, startIndex);
    let numberedPropertiesBeforePage = 0;
    let startLeaseIndexFrom = 0;

    for (const entry of itemsBeforePage) {
      if (entry.kind === "property") {
        if (!entry.item.isCurrentLocation) {
          numberedPropertiesBeforePage += 1;
        }
      } else {
        startLeaseIndexFrom += 1;
      }
    }

    return {
      pageProperties,
      pageLeaseComps,
      numberedPropertiesBeforePage,
      startLeaseIndexFrom,
      hasPageContent: pageProperties.length > 0 || pageLeaseComps.length > 0,
    };
  };

  return { totalItemsCount, totalPages, estimatedLegendHeight, getPageData };
};
